import React from 'react'
import { useSelector } from 'react-redux'

import BaseEntity from 'app/components/BaseEntity'
import Button from 'app/components/generic/Button'
import ButtonGroup from 'app/components/generic/ButtonGroup'
import constants, { ANNOUNCEMENT_STATUS_OPTIONS } from 'app/constants'
import { confirm } from 'app/helpers'
import {
  fetchAnnouncementsData,
  updateAnnouncementData
} from 'app/store/actions/communicationEntity'
import {
  dispatchUpdateCreateAnnouncementModalState,
  dispatchUpdateAnnouncementFormField,
  dispatchResetAnnouncementCreationData,
  dispatchUpdateAnnouncementStatus,
  dispatchUpdateReorderAnnouncementModalState
} from 'app/store/dispatcher'
import { selectAnnouncementsData } from 'app/store/selectors'

import CreateAnnouncement from './components/CreateAnnouncement'
import ReorderAnnouncement from './components/ReorderAnnouncement'
import { columns } from './config'

const Announcement = () => {
  const announcementsData = useSelector(selectAnnouncementsData)
  const [editingAnnouncementUuid, setEditingAnnouncementUuid] =
    React.useState(null)

  const handleNewAnnouncementClick = () => {
    dispatchResetAnnouncementCreationData()
    setEditingAnnouncementUuid(null)
    dispatchUpdateCreateAnnouncementModalState(true)
  }

  const handleReorderClick = () => {
    dispatchUpdateReorderAnnouncementModalState(true)
  }

  const handleModalClose = () => {
    setEditingAnnouncementUuid(null)
  }

  const CustomToolbarElement = (
    <>
      <Button style={{ marginRight: 10 }} onClick={handleReorderClick}>
        set sequence
      </Button>
      <Button onClick={handleNewAnnouncementClick}>New Announcement</Button>
    </>
  )

  const handleEditClick = (e, dataIndex) => {
    e.preventDefault()
    const announcementData = announcementsData?.data[dataIndex]
    console.log('announcements data', announcementData)

    // Set the UUID for editing
    setEditingAnnouncementUuid(announcementData?.uuid)

    // Pre-fill the form with the announcement data
    if (announcementData) {
      // Map the announcement data to form fields
      dispatchUpdateAnnouncementFormField('type', announcementData.type || '')
      dispatchUpdateAnnouncementFormField(
        'subtype',
        announcementData.subtype || ''
      )
      dispatchUpdateAnnouncementFormField(
        'batch_type',
        announcementData.batch_type || ''
      )
      dispatchUpdateAnnouncementFormField(
        'subject',
        announcementData.subject || null
      )
      dispatchUpdateAnnouncementFormField('title', announcementData.title || '')
      dispatchUpdateAnnouncementFormField(
        'description',
        announcementData.description || ''
      )
      dispatchUpdateAnnouncementFormField(
        'centres',
        announcementData.centres?.map((centre) => ({
          value: centre?.uuid,
          ...centre
        })) || []
      )
      dispatchUpdateAnnouncementFormField(
        'image',
        announcementData.image || null
      )
      dispatchUpdateAnnouncementFormField(
        'start_date',
        announcementData.start_date || ''
      )
      dispatchUpdateAnnouncementFormField(
        'end_date',
        announcementData.end_date || ''
      )
      dispatchUpdateAnnouncementFormField(
        'is_cta_present',
        !!(announcementData.cta || announcementData.cta_link)
      )
      dispatchUpdateAnnouncementFormField('cta', announcementData.cta || '')
      dispatchUpdateAnnouncementFormField(
        'cta_link',
        announcementData.cta_link || ''
      )
      dispatchUpdateAnnouncementFormField(
        'event_start_date',
        announcementData.timings?.start_date ||
          announcementData.event_start_date ||
          ''
      )
      dispatchUpdateAnnouncementFormField(
        'event_end_date',
        announcementData.timings?.end_date ||
          announcementData.event_end_date ||
          ''
      )
      dispatchUpdateAnnouncementFormField(
        'event_start_time',
        announcementData.timings?.start_time ||
          announcementData.event_start_time ||
          ''
      )
      dispatchUpdateAnnouncementFormField(
        'event_end_time',
        announcementData.timings?.end_time ||
          announcementData.event_end_time ||
          ''
      )
      dispatchUpdateAnnouncementFormField('tag_1', announcementData.tag_1 || '')
      dispatchUpdateAnnouncementFormField('tag_2', announcementData.tag_2 || '')
    }

    // Open the modal
    dispatchUpdateCreateAnnouncementModalState(true)
  }

  const handlePauseClick = (e, dataIndex) => {
    e.preventDefault()
    const announcementData = announcementsData?.data[dataIndex]

    if (!announcementData) return

    const { uuid, status } = announcementData

    // Determine the new status based on current status
    let newStatus
    let actionText = ''

    if (status === ANNOUNCEMENT_STATUS_OPTIONS.Paused) {
      // Resume logic - determine status based on dates
      const today = new Date()
      const endDate = new Date(announcementData.end_date)
      const startDate = new Date(announcementData.start_date)
      actionText = 'resume'

      if (endDate < today) {
        newStatus = ANNOUNCEMENT_STATUS_OPTIONS.Lapsed
      } else if (startDate > today) {
        newStatus = ANNOUNCEMENT_STATUS_OPTIONS.Upcoming
      } else {
        newStatus = ANNOUNCEMENT_STATUS_OPTIONS.Active
      }
    } else if (
      status === ANNOUNCEMENT_STATUS_OPTIONS.Active ||
      status === ANNOUNCEMENT_STATUS_OPTIONS.Upcoming
    ) {
      newStatus = ANNOUNCEMENT_STATUS_OPTIONS.Paused
      actionText = 'pause'
    } else {
      // For lapsed status, don't allow any changes
      return
    }

    // Show confirmation dialog
    confirm(
      () => {
        // User confirmed - dispatch the update status action
        dispatchUpdateAnnouncementStatus(uuid, newStatus)
      },
      `Are you sure you want to ${actionText} this announcement?`,
      () => {
        // User cancelled - do nothing
      },
      {
        confirmButtonText: `Yes, ${actionText}`,
        cancelButtonText: 'Cancel'
      }
    )
  }

  const getPauseButtonProps = (announcementData) => {
    const { status } = announcementData

    if (status === 'lapsed') {
      return {
        disabled: true,
        title: 'Pause',
        children: 'Pause'
      }
    } else if (status === 'paused') {
      return {
        disabled: false,
        title: 'Resume',
        children: 'Resume'
      }
    } else {
      // active or upcoming
      return {
        disabled: false,
        title: 'Pause',
        children: 'Pause'
      }
    }
  }
  const buttonStyles = {
    backgroundColor: '#EDC54A',
    textTransform: 'none',
    borderRadius: 4
  }

  const modifiedColumns = React.useMemo(() => {
    return [
      ...columns,
      {
        name: '',
        label: 'Actions',
        disabledInForm: true,
        options: {
          disableFilter: true,
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const announcementData = announcementsData?.data[dataIndex]
            const pauseButtonProps = getPauseButtonProps(announcementData)
            const isEditDisabled =
              announcementData?.status === ANNOUNCEMENT_STATUS_OPTIONS.Lapsed
            return (
              <>
                <Button
                  style={{ ...buttonStyles, marginRight: 10 }}
                  onClick={(e) => handlePauseClick(e, dataIndex)}
                  disabled={pauseButtonProps.disabled}
                  title={pauseButtonProps.title}>
                  {pauseButtonProps.children}
                </Button>
                <Button
                  style={buttonStyles}
                  onClick={(e) => handleEditClick(e, dataIndex)}
                  disabled={isEditDisabled}
                  title="Edit">
                  Edit
                </Button>
              </>
            )
          }
        }
      }
    ]
  }, [announcementsData])

  return (
    <>
      <BaseEntity
        entity={constants.ANNOUNCEMENT}
        label={constants.ANNOUNCEMENT_LABEL}
        columns={modifiedColumns}
        data={announcementsData}
        fetchData={fetchAnnouncementsData}
        updateEntityData={updateAnnouncementData}
        disableDelete
        readOnly
        CustomToolbarElement={CustomToolbarElement}
      />
      <CreateAnnouncement
        editingAnnouncementUuid={editingAnnouncementUuid}
        onModalClose={handleModalClose}
      />
      <ReorderAnnouncement
        onModalClose={() => dispatchUpdateReorderAnnouncementModalState(false)}
      />
    </>
  )
}

export default Announcement
